/**
 * AdminNoteEditor - Reusable full-page text editor for admin notes
 *
 * A simple textarea that fills available space, styled to match the app.
 * Used for freeform notes that don't require versioning.
 *
 * Usage:
 *   <AdminNoteEditor
 *     value={notes}
 *     onChange={setNotes}
 *     placeholder="Add notes..."
 *     autoSave={true}
 *     onSave={handleSave}
 *   />
 */

import React, { useCallback, useRef, useState } from "react";
import "../../css/components-forms.css";

function AdminNoteEditor({
  value = "",
  onChange,
  placeholder = "Add notes...",
  autoSave = false,
  onSave,
  disabled = false,
  minHeight = "300px",
}) {
  const [isDirty, setIsDirty] = useState(false);
  const textareaRef = useRef(null);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      onChange?.(newValue);
      setIsDirty(true);
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    if (autoSave && isDirty && onSave) {
      onSave(value);
      setIsDirty(false);
    }
  }, [autoSave, isDirty, onSave, value]);

  const handleKeyDown = useCallback(
    (e) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (onSave) {
          onSave(value);
          setIsDirty(false);
        }
      }
    },
    [onSave, value]
  );

  return (
    <div className="admin-note-editor" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <textarea
        ref={textareaRef}
        className="standard-textarea"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          flex: 1,
          minHeight,
          resize: "vertical",
          fontFamily: "var(--font-family-secondary)",
          fontSize: "var(--text-base)",
          lineHeight: "1.5",
          padding: "1rem",
        }}
      />
      {isDirty && autoSave && (
        <div
          style={{
            fontSize: "var(--text-xs)",
            color: "var(--color-medium-text)",
            marginTop: "0.5rem",
            textAlign: "right",
          }}
        >
          Unsaved changes • saves on blur or Ctrl+S
        </div>
      )}
    </div>
  );
}

export default AdminNoteEditor;
